import { useState } from 'react'
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts'
import { ClipboardCheck } from 'lucide-react'
import { ChartCard } from '@/components/common'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/common/ErrorState'
import { PeriodSelect } from './PeriodSelect'
import { useAttendanceOverview } from '../api'
import type { AttendanceSlice } from '../types'

/** Slice colour per status. Sourced from the design tokens, never Recharts defaults. */
const SLICE_COLORS: Record<AttendanceSlice['key'], string> = {
  PRESENT: 'var(--color-chart-present)',
  ON_LEAVE: 'var(--color-chart-leave)',
  ABSENT: 'var(--color-chart-absent)',
  HALF_DAY: 'var(--color-chart-halfday)',
}

const PERIODS = [
  { value: 'today', label: 'Today' },
  { value: 'month', label: 'This Month' },
]

/**
 * The attendance donut, with the total in the middle and a legend listing each status
 * with its count and share.
 */
export function AttendanceOverviewCard({ date }: { date?: string }) {
  const [period, setPeriod] = useState('month')
  const { data, isLoading, error, refetch } = useAttendanceOverview(date)

  const numberFormat = new Intl.NumberFormat(document.documentElement.lang || 'en')

  /**
   * A day nobody has been processed for is a real answer, and it is not an empty card.
   *
   * <p>Recharts draws nothing at all when every slice is zero, which left a labelled card
   * with a hole in it — read as a chart that failed to load rather than as "no attendance
   * has been recorded". A single neutral ring keeps the shape, and the centre still carries
   * the total, which is the number the reader came for.
   */
  const empty = data != null && data.slices.every((slice) => slice.count === 0)
  const slices = empty ? [{ key: 'EMPTY', label: 'Nothing recorded', count: 1 }] : data?.slices ?? []

  return (
    <ChartCard
      title="Attendance Overview"
      icon={ClipboardCheck}
      filter={<PeriodSelect value={period} options={PERIODS} onChange={setPeriod} label="Attendance period" />}
      footerLink={{ label: 'View Attendance Report', to: '/attendance' }}
    >
      {isLoading && <Skeleton className="mx-auto my-6 size-[180px] rounded-full" />}
      {error != null && <ErrorState error={error} onRetry={() => void refetch()} />}

      {data && (
        <div className="flex flex-col items-center gap-4 py-2 sm:flex-row sm:justify-between">
          <div className="relative size-[180px] shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="count"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius={58}
                  outerRadius={86}
                  paddingAngle={1}
                  startAngle={90}
                  endAngle={-270}
                  strokeWidth={0}
                  // The chart is decorative: the legend beside it already states every
                  // number, so screen readers are not made to walk the SVG.
                  isAnimationActive={false}
                >
                  {slices.map((slice) => (
                    <Cell
                      key={slice.key}
                      fill={empty
                        ? 'var(--color-border)'
                        : SLICE_COLORS[slice.key as AttendanceSlice['key']]}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            {/* Centred total, absolutely positioned over the donut hole. */}
            <div
              className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"
              aria-hidden="true"
            >
              <span className="text-xl font-semibold tabular-nums text-[var(--color-heading)]">
                {numberFormat.format(data.total)}
              </span>
              <span className="text-xs text-[var(--color-muted)]">Total</span>
            </div>
          </div>

          <ul className="w-full space-y-3 sm:w-auto sm:min-w-[170px]">
            {empty && (
              <li className="text-sm text-[var(--color-muted)]">
                Nothing has been processed for this day yet, which is not the same as everybody
                being absent.
              </li>
            )}
            {data.slices.map((slice) => (
              <li key={slice.key} className="flex items-center justify-between gap-4 text-sm">
                <span className="flex items-center gap-2 text-[var(--color-body)]">
                  <span
                    className="size-2 shrink-0 rounded-full"
                    style={{ backgroundColor: SLICE_COLORS[slice.key] }}
                    aria-hidden="true"
                  />
                  {slice.label}
                </span>
                <span className="tabular-nums text-[var(--color-heading)]">
                  {numberFormat.format(slice.count)}{' '}
                  <span className="text-[var(--color-muted)]">({slice.percent.toFixed(1)}%)</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </ChartCard>
  )
}
